import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { permissionsInterface } from "../onBoarding/onBoardingTypes";
import Grid, { GridColumn, GridHeader, GridRow } from "../../components/Grid/Grid";
import WebService from "../../utility/WebService";
import PageTitle from "../../components/PageTitle/PageTitle";
import HelperService from "../../utility/HelperService";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { PiExport } from "react-icons/pi";

const headers: GridHeader[] = [
  {
    title: "SubExpense Type",
    class: "text-left"
  },
  {
    title: "Payment mode",
    class: "text-left",
  },
  {
    title: "Amount",
    class: "text-left",
  },
  {
    title: "Comment",
    class: "text-left"
  },
];

const ExpensesReport = () => {
  const nextOffset = useRef<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<GridRow[]>([]);
  const rowCompute = useRef<GridRow[]>([]);
  const [showHiddenFields, setShowHiddenFields] = useState<boolean>(false);
  const [pressedKeys, setPressedKeys] = useState(new Set());
  const [expenseCount, setExpenseCount] = useState<number>(0);
  const token = useSelector((state: any) => state.OnBoardingReducer);
  const [permissions, setPermissions] = useState<permissionsInterface | null>(null);
  const [selectedExpenseType, setSelectedExpenseType] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date | any>("");
  const [endDate, setEndDate] = useState<Date | any>("");
  const [summaryBy, setSummaryBy] = useState<string>("");
  const [projectOptions, setProjectOptions] = useState<any[]>([]);
  const [expenseTypeList, setExpenseTypeList] = useState<any[]>([]);

  useEffect(() => {
    if (token && token.token && token.permissions && !!token.permissions.length) {
      setPermissions(token.permissions.find((item: any) => item.name === "EXPENSES_REPORT"));
      !projectOptions.length && getProjectList();
      !expenseTypeList.length && getExpenseTypeList();
    }
  }, [token])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pressedKeys]);

  useEffect(() => {
    showHiddenFields && getExpenseReportList(1);
  }, [showHiddenFields]);

  const getExpenseReportList = (offset: number, keyword?: string, startDate?: Date, endDate?: Date, order_by?: string, sort_by?: string) => {
    setSelectedExpenseType(sort_by ? sort_by : "");
    nextOffset.current = offset;
    if (nextOffset.current < 0 || isLoading) { return; }
    setIsLoading(true);
    WebService.getAPI({
      action: `expense-report/list?limit=10&offset=${offset - 1}&summary_by=${sort_by ?? ""}&from_date=${startDate ? startDate : ""}&to_date=${endDate ? endDate : ""}`,
      body: null,
      is_session: showHiddenFields
    })
      .then((res: any) => {
        setIsLoading(false);
        let rows: GridRow[] = [];
        setExpenseCount(res.count);
        setTotalAmount(res.totalAmount);
        for (var i in res.list) {
          let columns: GridColumn[] = [];
          columns.push({ value: res.list[i]?.sub_expense_type?.name ? res.list[i]?.sub_expense_type?.name : "N/A" });
          columns.push({ value: res.list[i].payment_mode_detail ? `${res.list[i].payment_mode_detail?.name} ${res.list[i].payment_mode_detail?.bank_name ? `(${res.list[i].payment_mode_detail?.bank_name})` : ""}` : "N/A" });
          columns.push({ value: res.list[i].amount ? res.list[i].amount : "N/A" });
          columns.push({ value: res.list[i].comment ? res.list[i].comment : "N/A" });
          rowCompute.current.push({ data: columns });
          rows.push({ data: columns });
        }
        rowCompute.current = rows;
        setRows(rowCompute.current);
        setStartDate(startDate ?? "");
        setEndDate(endDate ?? "");
        setSummaryBy(sort_by ? sort_by : "");
      })
      .catch((e) => {
        setIsLoading(false);
      });
  };

  const getExpenseTypeList = () => {
    WebService.getAPI({ action: `get/expense_type/list` })
      .then((res: any) => {
        if (res.list && res.list.length > 0) {
          setExpenseTypeList(
            res.list.map((item: any) => {
              return {
                ...item,
                ...{ value: item.id, label: item.name },
              };
            })
          );
        }
      })
      .catch((e: any) => { });
  };

  const onSortList = (offset: number, isAscending?: boolean, key?: string) => {
    getExpenseReportList(
      offset,
      null as any,
      null as any,
      null as any,
      isAscending ? "asc" : "desc",
      key
    );
  };

  const handleKeyDown = (e: any) => {
    const key = e.key.toLowerCase();
    setPressedKeys((prev: any) => new Set([...prev, key]));
  };

  const handleKeyUp = (e: any) => {
    const key = e.key.toLowerCase();
    if (pressedKeys.has('control') && pressedKeys.has('alt') && pressedKeys.has('o') && pressedKeys.has('p')) { setShowHiddenFields(true); }
    setPressedKeys((prev: any) => { const updated = new Set(prev); updated.delete(key); return updated; });
  };

  const exportExpenseReport = () => {
    WebService.getAPI({
      action: `expense-report/export?fromDate=${startDate ? startDate : ""}&toDate=${endDate ? endDate : ""}&summary_by=${summaryBy ? summaryBy : ""}`,
      body: null,
      responseType: 'arraybuffer',
    }).then((response: any) => {
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense_report_${new Date().toISOString()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => {
      toast.error("Failed to export expense report");
    });
  };

  const getProjectList = () => {
    WebService.getAPI({
      action: "get/project/list",
    }).then((res: any) => {
      const projectList = res.list.map((item: any) => {
        return { ...item, value: item.id, label: item.project_name };
      });
      projectList.unshift({ value: null, label: "Admin" });
      setProjectOptions(projectList);
    });
  };


  return (
    <>
      {permissions && permissions.can_read && <div className="app-page page-dashboard">
        <div className="d-flex justify-content-between align-items-center ">
          <PageTitle title="Expenses Report" backArrow={false} active={showHiddenFields} />
          {/* <Button className="btn btn-brand-1 mb-3">{" "} <Plus size={20} className="ms-1" /> Add Expense </Button> */}
          {permissions && permissions.can_write && permissions.can_update && <Button
            className="btn btn-brand-1 mb-3 ms-2"
            onClick={() => { exportExpenseReport(); }}
          >
            <PiExport size={20} className="ms-1" /> Export
          </Button>}
        </div>
        <div className="table-card">

          <Grid
            rows={rows}
            headers={headers}
            showDateFilter={true}
            onPageChange={getExpenseReportList}
            errorMessage={"No Expense Found"}
            ShowLoader={isLoading}
            onSort={onSortList}
            count={expenseCount}
            isShowExpenseTypeFilter={true}
            expenseTypeOptions={expenseTypeList}
            selectedExpenseType={selectedExpenseType}
            isShowProjectFilter
            projectOptions={projectOptions}
          />
          <div className="font-18 font-medium">
            <span>Total Expense Amount :- {HelperService.getCurrencyFormatter(totalAmount)}</span>
          </div>
        </div>
      </div>}
    </>
  );
};

export default ExpensesReport;
